import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSettingsContext } from '../../context/SettingsContext';
import usePermission from '../../hooks/usePermission';
import { usePreorderEnabled } from '../../hooks/usePreorderEnabled';
import { getDocs, query, where } from '../../libs/firestore-wrapper';
import { getAllCatagories, getAllFoods } from '../../modals/order';
import { IFoodMenus } from '../../pages/MenuManagement/menu.types';
import { useCategoriesContext } from '../CategoriesContext';

interface MenuContextValue {
  foodMenu: IFoodMenus;
  setFoodMenu: React.Dispatch<React.SetStateAction<IFoodMenus>>;
  loading: boolean;
}

const init_food_menu = {
  categories: [],
  foods: [],
};

const MenuContext = createContext<MenuContextValue>({
  foodMenu: init_food_menu,
  setFoodMenu: () => {},
  loading: true,
});

const MenuProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(true);
  let [foodMenu, setFoodMenu] = useState<IFoodMenus>(init_food_menu);
  const { restaurantId } = usePermission();
  const { enabledNewVersion } = usePreorderEnabled();
  const { foodMenu: categoriesFoodMenu } = useCategoriesContext();
  const { loading: settingsLoading } = useSettingsContext();

  const fetchData = useCallback(async () => {
    try {
      if (settingsLoading) return;
      setLoading(true);

      if (enabledNewVersion) {
        if (categoriesFoodMenu && categoriesFoodMenu.foods) {
          const filteredFoods = restaurantId
            ? (categoriesFoodMenu.foods as any[]).filter((f) => f.restaurant_id === restaurantId || f.restaurantId === restaurantId)
            : (categoriesFoodMenu.foods as any[]);

          setFoodMenu({
            categories: categoriesFoodMenu.categories,
            foods: filteredFoods,
          } as IFoodMenus);
        }
        setLoading(false);
        return;
      }

      if (restaurantId) {
        const data = (await fetchFoodMenu(restaurantId)) as IFoodMenus;
        data.foods.sort((a, b) => {
          if (a.create_time && b.create_time) {
            const aTime = a.create_time.seconds * 1000 + a.create_time.nanoseconds / 1000000;
            const bTime = b.create_time.seconds * 1000 + b.create_time.nanoseconds / 1000000;
            return bTime - aTime;
          } else if (a.create_time) {
            return -1;
          } else if (b.create_time) {
            return 1;
          } else {
            return 0;
          }
        });
        // ensure foods belong to this restaurant
        const filtered = data.foods.filter((f: any) => f.restaurant_id === restaurantId || f.restaurantId === restaurantId);
        setFoodMenu({ ...data, foods: filtered });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, enabledNewVersion, categoriesFoodMenu, settingsLoading]);

  async function fetchFoodMenu(_restaurantId: string) {
    try {
      const categoryQuery = getAllCatagories(_restaurantId);
      const categoryQuerySnapshot = await getDocs(categoryQuery);
      const categoryDocs = categoryQuerySnapshot.docs;
      const categoryIds = categoryDocs.map((categoryDoc: any) => categoryDoc.id);

      const foodChunks = chunkArray(categoryIds, 20);

      const foodPromises = foodChunks.map(async (chunk) => {
        const baseFoodQuery = getAllFoods(_restaurantId);
        const foodQuery = query(baseFoodQuery, where('categories', 'array-contains-any', chunk));
        const foodQuerySnapshot = await getDocs(foodQuery);
        return foodQuerySnapshot.docs.map((foodDoc: any) => ({ id: foodDoc.id, ...foodDoc.data() }));
      });

      const foodChunksResults = await Promise.all(foodPromises);

      const allFoods = foodChunksResults.reduce((acc: any, chunkResult: any) => {
        acc.push(...chunkResult);
        return acc;
      }, []);

      const result = {
        categories: categoryDocs.map((categoryDoc: any) => categoryDoc.data()),
        foods: allFoods,
      };
      return result;
    } catch (error) {
      throw error;
    }
  }

  function chunkArray(array: any, size: number) {
    const chunkedArr = [];
    let index = 0;
    while (index < array.length) {
      chunkedArr.push(array.slice(index, size + index));
      index += size;
    }
    return chunkedArr;
  }

  useEffect(() => {
    if (restaurantId && !settingsLoading) {
      fetchData();
    }
  }, [restaurantId, settingsLoading, fetchData]);

  return (
    <MenuContext.Provider
      value={{
        loading,
        foodMenu,
        setFoodMenu,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

const useMenuContext = () => {
  const context = useContext(MenuContext);
  return context;
};

export { MenuProvider, useMenuContext };

